import { ConsentResponse, EngagementTask, EngagementTaskStatus, EngagementTaskType } from '@aiwyn/api';
import { AuthService } from '@aiwyn/auth';
import { Annotation, Logger, SharedModule, ViewAndSignPdfComponent } from '@aiwyn/shared';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { ChangeDetectorRef, Component, QueryList, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { ConsentValues } from '../../models';
import { ConfirmConsentDocument, ConfirmConsentDocumentMultiContact, EngagementsState, SaveConsentDocument, SaveConsentDocumentMultiContact } from '../../state';
import { AbstractApryseStep } from '../abstract-apryse-step/abstract-apryse-step';
import { DocumentFieldType, getAnnotationDocumentContactId, getFieldDocumentContactId, getFieldType } from '../abstract-apryse-step/apryse-utils';

@Component({
  selector: 'app-consent-step',
  templateUrl: '../abstract-apryse-step/abstract-apryse-step.html',
  styleUrls: [
    './consent-step.component.scss',
    '../abstract-apryse-step/abstract-apryse-step.scss',
  ],
  standalone: true,
  imports: [
    SharedModule,
    NgClass,
    AsyncPipe,
  ],
})
export class ConsentStepComponent extends AbstractApryseStep {

  @ViewChildren(ViewAndSignPdfComponent) pdfViewer: QueryList<ViewAndSignPdfComponent>;

  constructor(
    readonly store: Store,
    readonly changeDetectorRef: ChangeDetectorRef,
    readonly datePipe: DatePipe,
    dialog: MatDialog,
    breakpointObserver: BreakpointObserver,
    readonly authService: AuthService,
    readonly logger: Logger,
  ) {
    super(store, changeDetectorRef, datePipe, dialog, breakpointObserver, authService, logger);
  }

  getSubmitActionForTask(task: EngagementTask, updatedEngagementLetterData: string): any {
    const isMultiIdentity = this.store.selectSnapshot(EngagementsState.isMultipleIdentitySigningActive);

    if (isMultiIdentity) {
      const documentInfo = this.getCurrentDocumentInfo();
      const allUserDocContactIds = this.store.selectSnapshot(EngagementsState.getAllUserDocumentContactIds(documentInfo.documentId));
      const fieldValues = this._fieldValues$.value;

      const householdConsentKeys = Object
        .keys(fieldValues)
        .filter(key =>
          getFieldType(key) === DocumentFieldType.CONSENT
            && allUserDocContactIds.includes(getFieldDocumentContactId(key))
        );

      const everyConsentIsOff = householdConsentKeys.length > 0
        && householdConsentKeys.every(key => fieldValues[key] === ConsentValues.OFF);

      if (everyConsentIsOff) {
        return new ConfirmConsentDocumentMultiContact();
      }

      const currentUserKey = householdConsentKeys
        .find(key => getFieldDocumentContactId(key) === this.getCurrentUserDocumentContactId());

      const consentValue = fieldValues[currentUserKey] === ConsentValues.AFFIRMATIVE
        ? ConsentResponse.CONSENT_GIVEN
        : ConsentResponse.CONSENT_DENIED;

      return new SaveConsentDocumentMultiContact(updatedEngagementLetterData, consentValue);
    }

    const fieldKey = Object.keys(this._fieldValues$.value)
      .find(key =>
        getFieldType(key) === DocumentFieldType.CONSENT
        && getFieldDocumentContactId(key) === this.getCurrentUserDocumentContactId()
      );

    // There is a difference between not responding and denying consent
    if (this._fieldValues$.value[fieldKey] === ConsentValues.OFF) {
      return new ConfirmConsentDocument(updatedEngagementLetterData);
    }

    const consentValue = this._fieldValues$.value[fieldKey] === ConsentValues.AFFIRMATIVE
      ? ConsentResponse.CONSENT_GIVEN
      : ConsentResponse.CONSENT_DENIED;

    return new SaveConsentDocument(updatedEngagementLetterData, consentValue);
  }

  getNecessaryTasks(userTasks: EngagementTask[]): EngagementTask[] {
    const provideConsentTask = userTasks.find(task => task.type === EngagementTaskType.PROVIDE_CONSENT || task.type === EngagementTaskType.PROVIDE_ADD_ON_TASK);
    return provideConsentTask?.status === EngagementTaskStatus.REQUIRED ? [provideConsentTask] : userTasks;
  }

  /**
   * Consent documents are always generated with a consent block / signature for each user associated with the engagement letter, regardless
   * of the signature requirement type; always filter by the current user's document contact id.
   * @param annotations
   */
  filterAdditionalAnnotationsByStep(annotations: Annotation[]): Annotation[] {
    const isMultiIdentity = this.store.selectSnapshot(EngagementsState.isMultipleIdentitySigningActive);

    if (isMultiIdentity) {
      const documentInfo = this.getCurrentDocumentInfo();
      const allUserDocContactIds = this.store.selectSnapshot(EngagementsState.getAllUserDocumentContactIds(documentInfo.documentId));
      
      return annotations.filter(annotation => allUserDocContactIds.includes(getAnnotationDocumentContactId(annotation)));
    }

    const currentUserDocumentContactId = this.getCurrentUserDocumentContactId();
    return annotations.filter(annotation => {
      return getAnnotationDocumentContactId(annotation) === currentUserDocumentContactId;
    });
  }
}
